import React, { FC } from 'react';
import { ButtonV2, CloseButton, Flex, styled, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { closeButtonOverrides, drawerOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { buttonSharedStates } from '@shared/utils/style/buttons';

type PrivacyAlertDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onCTAClick: ({ makeCollectionPublic }: { makeCollectionPublic: boolean }) => void;
  disabled?: boolean;
};

export const StyledActionButton = styled(ButtonV2)`
  ${buttonSharedStates};
`;

export const PrivacyAlertDialog: FC<PrivacyAlertDialogProps> = props => {
  const { isOpen, onClose, onCTAClick, disabled } = props;

  const { t } = useTranslation('joykit');
  const photoPickerTransFn = t('photoPicker');

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={onClose}
      hideDivider={true}
      removeBodyPadding
      hasCloseButton={false}
      drawerOverrides={{
        ...drawerOverrides,
        Content: {
          props: {
            ...drawerOverrides.Content?.props,
            boxShadow: '0px -7px 16px -8px rgba(0, 0, 0, 0.3), 0px -10px 27px -5px rgba(44, 41, 37, 0.25)',
          },
        },
        Body: { props: { ...drawerOverrides.Body?.props, paddingY: 8 } },
      }}
      dialogOverrides={{
        Content: { props: { width: ['100%', '540px'] } },
        Body: { props: { paddingX: 9, paddingY: 9 } },
      }}
    >
      <Flex
        flexDirection="column"
        width="100%"
        paddingY={6}
      >
        <CloseButton
          overrides={{
            Root: { props: { ...closeButtonOverrides.Root?.props, position: 'absolute', top: 5, right: 5 } },
            Icon: { props: { ...closeButtonOverrides.Icon?.props } },
          }}
          onClick={onClose}
          aria-label={'close dialog'}
        />

        <Flex
          flexDirection="column"
          alignItems="center"
          gap={4}
        >
          <TextV2
            typographyVariant="hed6"
            textAlign="center"
            color="mono14"
          >
            {photoPickerTransFn.alertHiddenCollectionTitle()}
          </TextV2>
          <TextV2
            typographyVariant="body2"
            textAlign="center"
            color="mono14"
          >
            {photoPickerTransFn.alertHiddenCollectionSubtitle()}
          </TextV2>
        </Flex>

        <Flex
          flexDirection="column"
          width="100%"
          gap={4}
          marginTop={7}
          alignItems="center"
        >
          <StyledActionButton
            intent="neutral"
            size="lg"
            variant="solid"
            shape="rounded"
            width="100%"
            maxWidth={pxToRem(380)}
            onClick={() => onCTAClick({ makeCollectionPublic: true })}
            disabled={disabled}
          >
            {photoPickerTransFn.changeToPublicCTA()}
          </StyledActionButton>
          <StyledActionButton
            color="mono14"
            size="lg"
            variant="ghost"
            width="100%"
            maxWidth={pxToRem(380)}
            onClick={() => onCTAClick({ makeCollectionPublic: false })}
            disabled={disabled}
          >
            {photoPickerTransFn.keepPrivateCTA()}
          </StyledActionButton>
        </Flex>
      </Flex>
    </ResponsiveDialog>
  );
};

PrivacyAlertDialog.displayName = 'PrivacyAlertDialog';
